import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { DatabaseBackup, Loader2, MoreVertical, Plus } from "lucide-react";
import { API, authHdr } from "./AssociateFields";
import { LegacyMigrationDialog } from "./LegacyMigrationDialog";
import { EmployeeIndex, STATUS_CLS } from "./EmployeeIndex";
import { AssocList } from "./AssocList";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Not Login Yet");

const ActionMenu = ({ a, onLogs, onDiscard, onBlock }) => {
  const nav = useNavigate();
  const go = (mode) => nav(`/sub-brokers/associate/${a.id}?mode=${mode}`);
  const editable = !a.is_legacy && a.status !== "Pending for Associate Verification";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`associate-actions-${a.id}`}><MoreVertical className="h-4 w-4" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {!a.is_legacy && <DropdownMenuItem onClick={() => go("view")} data-testid="associate-action-view">View Details</DropdownMenuItem>}
        <DropdownMenuItem onClick={() => onLogs(a)} data-testid="associate-action-logs">View Logs</DropdownMenuItem>
        {a.status === "Pending for Superuser Verification" && <DropdownMenuItem onClick={() => go("verify")} data-testid="associate-action-verify">Verify</DropdownMenuItem>}
        {editable && <DropdownMenuItem onClick={() => go("edit")} data-testid="associate-action-edit">Edit</DropdownMenuItem>}
        {a.status === "Associate Creation Pending" ? <DropdownMenuItem onClick={() => onDiscard(a)} className="text-red-600" data-testid="associate-action-discard">Discard draft</DropdownMenuItem>
          : a.can_delete && <DropdownMenuItem onClick={() => onDiscard(a)} className="text-red-600" data-testid="associate-action-delete">Delete (trial record)</DropdownMenuItem>}
        {a.is_legacy && <DropdownMenuItem onClick={() => nav(`/sub-brokers/edit/${a.id}`)} data-testid="associate-action-legacy-edit">Edit (existing record)</DropdownMenuItem>}
        {a.status !== "Associate Creation Pending" && <DropdownMenuItem onClick={() => onBlock(a)} className={a.login_blocked ? "text-emerald-700" : "text-red-600"} data-testid="associate-action-block">{a.login_blocked ? "Make Active (allow login)" : "Make Inactive (block login)"}</DropdownMenuItem>}
        {a.status === "Active" && <DropdownMenuItem disabled data-testid="associate-action-update">Update · Reset Password Link (Phase 2)</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function AssociateIndex() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState(null);
  const [creating, setCreating] = useState(false);
  const [mig, setMig] = useState(false);

  const [empSlot, setEmpSlot] = useState(null);
  const [tab, setTab] = useState(() => { const t = new URLSearchParams(window.location.search).get("tab"); return ["employee", "introducer"].includes(t) ? t : "associate"; });
  const load = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const r = await axios.get(`${API}/associates/list`, { headers: authHdr() });
      setRows(r.data.associates || []);
    } catch (e) { if (!quiet) toast.error(e.response?.data?.detail || "Failed to load associates"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); axios.get(`${API}/associates/settings/emails`, { headers: authHdr() }).then((r) => setPaused(r.data.paused)).catch(() => {}); }, []); // eslint-disable-line
  useEffect(() => { const f = () => load(true); window.addEventListener("focus", f); return () => window.removeEventListener("focus", f); }, []); // eslint-disable-line
  const [paused, setPaused] = useState(null);
  const togglePause = async (v) => {
    try { await axios.put(`${API}/associates/settings/emails`, { paused: v }, { headers: authHdr() }); setPaused(v); toast.success(v ? "Emails to associates paused" : "Emails to associates turned back on"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not change"); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/associates/draft`, {}, { headers: authHdr() });
      nav(`/sub-brokers/associate/${r.data.id}?mode=edit`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); setCreating(false); }
  };
  const discard = async (a) => {
    if (!window.confirm(a.status === "Associate Creation Pending" ? `Discard draft "${a.name}"?` : `Delete trial record "${a.name}" (${a.code || "no code"})? This removes it from the list.`)) return;
    try { await axios.delete(`${API}/associates/${a.id}`, { headers: authHdr() }); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Discard failed"); }
  };
  const block = async (a) => {
    const blocked = !a.login_blocked;
    let reason = "";
    if (blocked) {
      reason = window.prompt(`Make "${a.name}" Inactive? They and their employees won't be able to sign in until made Active again. Data and commissions stay as they are. Reason (optional):`, "");
      if (reason === null) return;
    } else if (!window.confirm(`Make "${a.name}" Active and allow sign in again?`)) return;
    try {
      await axios.post(`${API}/associate-extras/${a.id}/login-block`, { blocked, reason }, { headers: authHdr() });
      toast.success(blocked ? "Marked Inactive — login blocked" : "Marked Active — login allowed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update login"); }
  };
  const Menu = ({ a }) => <ActionMenu a={a} onLogs={openLogs} onDiscard={discard} onBlock={block} />;
  const openLogs = async (a) => {
    const r = await axios.get(`${API}/associates/${a.id}/logs`, { headers: authHdr() });
    setLogs({ name: a.name, rows: r.data.logs || [] });
  };

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="associate-index">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-slate-900">Associates</h1>
        <label className="mr-auto flex cursor-pointer select-none items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-800" title="While ticked, associates and their employees get no emails when you approve or reject their details">
          <input type="checkbox" checked={!!paused} disabled={paused === null} onChange={(e) => togglePause(e.target.checked)} className="accent-amber-600" data-testid="associate-emails-paused" />
          Pause emails to associates
        </label>
        {tab !== "employee" && <div className="flex flex-wrap gap-2">
          <Link to="/sub-brokers/bulk-upload"><Button variant="outline" size="sm">Bulk upload</Button></Link>
          <Button variant="outline" size="sm" onClick={() => setMig(true)} data-testid="open-migration-btn"><DatabaseBackup className="mr-1.5 h-4 w-4" />Move from old Kinntegra</Button>
          <Button onClick={create} disabled={creating} data-testid="new-associate-btn">
            {creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}New Associate
          </Button>
        </div>}
        {tab === "employee" && <div ref={setEmpSlot} className="flex flex-wrap gap-2" data-testid="employee-header-actions" />}
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList><TabsTrigger value="associate" data-testid="tab-associate">Associate</TabsTrigger>
          <TabsTrigger value="introducer" data-testid="tab-introducer">Introducer</TabsTrigger>
          <TabsTrigger value="employee" data-testid="tab-employee">Employee</TabsTrigger></TabsList>
        <TabsContent value="associate"><AssocList kind="associate" rows={rows.filter((x) => !x.is_introducer)} loading={loading} statusCls={STATUS_CLS} Menu={Menu} /></TabsContent>
        <TabsContent value="introducer"><AssocList kind="introducer" rows={rows.filter((x) => x.is_introducer)} loading={loading} statusCls={STATUS_CLS} Menu={Menu} /></TabsContent>
        <TabsContent value="employee"><EmployeeIndex actionsEl={empSlot} /></TabsContent>
      </Tabs>

      <Dialog open={!!logs} onOpenChange={(o) => !o && setLogs(null)}>
        <DialogContent className="max-w-3xl" data-testid="associate-logs-dialog">
          <DialogHeader><DialogTitle>Associate Status History — {logs?.name}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm"><thead><tr className="border-b text-left text-xs uppercase text-slate-500">
              <th className="py-2 pr-3">Date &amp; Time</th><th className="pr-3">Status</th><th className="pr-3">Action By</th><th>Remarks</th></tr></thead>
              <tbody>{(logs?.rows || []).map((l) => (
                <tr key={l.id} className="border-b last:border-0"><td className="py-2 pr-3 text-xs">{fmt(l.at)}</td><td className="pr-3">{l.status}</td>
                  <td className="pr-3">{l.by}</td><td>{l.remarks || l.action}</td></tr>))}
                {!logs?.rows?.length && <tr><td colSpan={4} className="py-6 text-center text-slate-500">No history recorded (existing associate)</td></tr>}
              </tbody></table>
          </div>
        </DialogContent>
      </Dialog>
      <LegacyMigrationDialog open={mig} onClose={() => setMig(false)} onDone={load} />
    </div>
  );
}
